<template>
  <transition name="toast">
    <div
      v-if="showErrors"
      class="toast-alert toast-alert--error"
      role="alert"
      @mouseenter="pauseTimer"
      @mouseleave="resumeTimer"
    >
      <div class="toast-alert__icon">
        <i class="bi bi-exclamation-octagon-fill"></i>
      </div>
      <div class="toast-alert__body">
        <div class="toast-alert__title">Ocurrió un error</div>
        <div class="toast-alert__messages">
          <div
            v-for="(errorMessages, fieldName) in showErrors"
            :key="fieldName"
            class="toast-alert__group"
          >
            <span
              v-for="message in errorMessages"
              :key="message"
              class="toast-alert__message"
            >
              {{ message }}
            </span>
          </div>
        </div>
      </div>
      <button type="button" class="toast-alert__close" aria-label="Cerrar" @click="clearb">
        <i class="bi bi-x-lg"></i>
      </button>
      <div class="toast-alert__progress">
        <div
          class="toast-alert__progress-bar"
          :style="progressStyle"
        ></div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const AUTO_DISMISS_MS = 5000;

export default {
  name: 'ErrorApi',
  data() {
    return {
      timerId: null,
      remaining: AUTO_DISMISS_MS,
      startedAt: null,
      animating: false,
    };
  },
  computed: {
    ...mapGetters({
      showErrors: 'errors/showErrors'
    }),
    progressStyle() {
      return {
        animationDuration: `${AUTO_DISMISS_MS}ms`,
        animationPlayState: this.animating ? 'running' : 'paused',
      };
    },
  },
  watch: {
    showErrors(val) {
      if (val) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    },
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    ...mapActions({
      clear: 'errors/clearErrors'
    }),
    clearb() {
      this.clear();
    },
    startTimer() {
      this.stopTimer();
      this.remaining = AUTO_DISMISS_MS;
      this.startedAt = Date.now();
      this.animating = false;
      this.$nextTick(() => { this.animating = true; });
      this.timerId = setTimeout(this.clearb, this.remaining);
    },
    pauseTimer() {
      if (!this.timerId) return;
      clearTimeout(this.timerId);
      this.timerId = null;
      this.remaining -= Date.now() - this.startedAt;
      this.animating = false;
    },
    resumeTimer() {
      if (this.timerId || !this.showErrors) return;
      this.startedAt = Date.now();
      this.animating = true;
      this.timerId = setTimeout(this.clearb, Math.max(this.remaining, 0));
    },
    stopTimer() {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      this.animating = false;
    },
  }
}
</script>

<style scoped>
.toast-alert {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: min(420px, calc(100vw - 48px));
  padding: 14px 16px 16px 16px;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08);
  border-left: 4px solid #dc3545;
  overflow: hidden;
  font-family: inherit;
}

.toast-alert--error {
  background: #fff5f6;
  border-left-color: #dc3545;
}

.toast-alert__icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fde2e4;
  color: #c0202f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
}

.toast-alert__body {
  flex: 1 1 auto;
  min-width: 0;
}

.toast-alert__title {
  font-weight: 700;
  font-size: 14px;
  color: #6a1620;
  margin-bottom: 4px;
}

.toast-alert__messages {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toast-alert__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toast-alert__message {
  display: block;
  font-size: 13px;
  line-height: 1.4;
  color: #491217;
  word-break: break-word;
}

.toast-alert__close {
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  color: #6a1620;
  opacity: 0.6;
  font-size: 14px;
  line-height: 1;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: opacity 150ms ease, background-color 150ms ease;
}

.toast-alert__close:hover {
  opacity: 1;
  background: rgba(106, 22, 32, 0.08);
}

.toast-alert__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(220, 53, 69, 0.15);
  overflow: hidden;
}

.toast-alert__progress-bar {
  width: 100%;
  height: 100%;
  background: #dc3545;
  transform-origin: left center;
  animation-name: toast-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes toast-progress {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

.toast-enter-active,
.toast-leave-active {
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease;
}
.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
</style>
